import {
  BrowserTracing,
  init,
  reactRouterV6Instrumentation,
  Replay,
} from '@sentry/react';
import 'typeface-roboto';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import './index.css';
import { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import {
  BrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { fromJS } from 'immutable';
import reportWebVitals from './reportWebVitals';
import store from './services/store';
import debug from './services/debug';
import AppProvider from './components/app-provider/container';
import invariant from './utils/invariant';

// Quickly reload window if user has stale version of the app running
window.addEventListener('error', (event) => {
  const target = event.target as HTMLElement | null;

  if (target) {
    const isScript =
      target.tagName === 'SCRIPT' &&
      (target as HTMLScriptElement).src.includes('/assets/');

    const isCSS =
      target.tagName === 'LINK' &&
      (target as HTMLLinkElement).href.includes('/assets/');

    if (isScript || isCSS) {
      // eslint-disable-next-line no-console
      console.warn('Detected stale asset. Reloading app...');

      window.location.reload();
    }
  }
});

// Handle dynamic import failures (Promise rejections) for stale assets
window.addEventListener('unhandledrejection', (event) => {
  const error = event.reason as Error;
  const errorMessage = error?.message || String(error || '');
  const errorStack = error?.stack || '';

  const isStaleAssetError =
    errorMessage.includes('Failed to fetch dynamically imported module') &&
    (errorMessage.includes('/assets/') || errorStack.includes('/assets/'));

  if (isStaleAssetError) {
    event.preventDefault();

    // eslint-disable-next-line no-console
    console.warn(
      'Detected stale dynamically imported module. Reloading app...',
    );

    window.location.reload();
  }
});

// Safari polyfill for React PDF
if (typeof Promise.withResolvers !== 'function') {
  Promise.withResolvers = <T,>() => {
    let resolve!: (value: T | PromiseLike<T>) => void;
    let reject!: (reason?: unknown) => void;

    const promise = new Promise<T>((res, rej) => {
      resolve = res;
      reject = rej;
    });

    return {
      promise,
      resolve,
      reject,
    };
  };
}

// Sentry
const { VITE_APP_DOMAIN, VITE_APP_ENV, VITE_APP_VERSION } = import.meta.env;

invariant(VITE_APP_DOMAIN);
invariant(VITE_APP_VERSION);

if (window.location.href.indexOf('localhost') === -1) {
  init({
    dsn: 'https://f5eacc3b184b76fa3ef32edb0085b412@o1418780.ingest.sentry.io/4505753588465664',
    environment: VITE_APP_ENV,
    integrations: [
      new BrowserTracing({
        routingInstrumentation: reactRouterV6Instrumentation(
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        ),
      }),
      new Replay(),
    ],
    tracesSampleRate: 0.1,
    profilesSampleRate: 0.1,
    tracePropagationTargets: [VITE_APP_DOMAIN],
    release: VITE_APP_VERSION,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

if (
  import.meta.env.VITE_APP_DEBUG_STORE === 'true' ||
  sessionStorage.getItem('TestMode') === 'true'
) {
  const listenToStore = () => {
    const state = fromJS(store.getState());

    debug('Redux Store Changed', state.toJS());
  };
  store.subscribe(listenToStore);
}

const container = document.getElementById('root') as HTMLElement;

const root = createRoot(container);

root.render(
  <Provider store={store}>
    <BrowserRouter>
      <AppProvider />
    </BrowserRouter>
  </Provider>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
